/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect } from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Grid, PageGridContainer } from '@coursera/cds-core';

import { NotFound } from 'bundles/common/components/withNotFound';
import CourseCardsSection from 'bundles/partners-graphql/components/cds/CourseCardsSection';
import DegreeCardsSection from 'bundles/partners-graphql/components/cds/DegreeCardsSection';
import ExploreCategoriesSection from 'bundles/partners-graphql/components/cds/ExploreCategoriesSection';
import InstructorsCarouselSection from 'bundles/partners-graphql/components/cds/InstructorsCarouselSection';
import Loading from 'bundles/partners-graphql/components/cds/Loading';
import PartnerHero from 'bundles/partners-graphql/components/cds/PartnerHero';
import PartnerLinksSection from 'bundles/partners-graphql/components/cds/PartnerLinks';
import SpecializationCardsSection from 'bundles/partners-graphql/components/cds/SpecializationCardsSection';
import PartnerCoursesQuery from 'bundles/partners-graphql/components/cds/queries/PartnerCoursesQuery.graphql';
import PartnerDegreesQuery from 'bundles/partners-graphql/components/cds/queries/PartnerDegreesQuery.graphql';
import PartnerDomainsQuery from 'bundles/partners-graphql/components/cds/queries/PartnerDomainsQuery.graphql';
import PartnerInfoQuery from 'bundles/partners-graphql/components/cds/queries/PartnerInfoQuery';
import PartnerSpecializationsQuery from 'bundles/partners-graphql/components/cds/queries/PartnerSpecializationsQuery.graphql';
import type {
  CdsPartnerInfoQuery,
  CdsPartnerInfoQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/CdsPartnerInfoQuery';
import type {
  PartnerCoursesQueryQuery,
  PartnerCoursesQueryQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerCoursesQuery';
import type {
  PartnerDegreesQueryQuery,
  PartnerDegreesQueryQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerDegreesQuery';
import type {
  PartnerDomainsQueryQuery,
  PartnerDomainsQueryQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerDomainsQuery';
import type {
  PartnerSpecializationsQueryQuery,
  PartnerSpecializationsQueryQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerSpecializationsQuery';
import PartnerPageGoogleSchemaMarkup from 'bundles/partners-graphql/components/seo/PartnerPageGoogleSchemaMarkup';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import { getSubdomainDetails } from 'bundles/seo/utils/domainAndMetadataUtils';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/partners-graphql';

type PropsFromCaller = {
  params: {
    shortName: string;
  };
};

const PartnerPage = ({ params: { shortName } }: PropsFromCaller) => {
  const location = useLocation();

  // Query for partner info
  const {
    data: partnerData,
    loading: partnerLoading,
    error: partnerError,
  } = useQuery<CdsPartnerInfoQuery, CdsPartnerInfoQueryVariables>(PartnerInfoQuery, {
    variables: {
      shortName,
    },
  });

  const partner = partnerData?.PartnersV1Resource?.shortName?.partners?.[0];
  const courseIds = (partner?.courseIds ?? []).slice(0, 1000);
  const specializationIds = partner?.specializations?.elements?.flatMap((s) => (s ? [s.id] : [])) ?? [];

  // Query for course details from gateway
  const { data: coursesData, loading: coursesLoading } = useQuery<
    PartnerCoursesQueryQuery,
    PartnerCoursesQueryQueryVariables
  >(PartnerCoursesQuery, {
    variables: {
      courseIds,
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: courseIds.length === 0,
  });

  const courses = coursesData?.Course?.queryByIds ?? [];

  // Query for specialization details from gateway
  const { data: specializationsData, loading: specializationsLoading } = useQuery<
    PartnerSpecializationsQueryQuery,
    PartnerSpecializationsQueryQueryVariables
  >(PartnerSpecializationsQuery, {
    variables: {
      specializationIds,
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: specializationIds.length === 0,
  });

  const specializations = specializationsData?.Specialization?.queryByIds ?? [];

  // Query for degrees by partner ID
  const { data: degreesData, loading: degreesLoading } = useQuery<
    PartnerDegreesQueryQuery,
    PartnerDegreesQueryQueryVariables
  >(PartnerDegreesQuery, {
    variables: {
      partnerId: partner?.id ?? '',
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: !partner?.id,
  });

  const allDegrees = degreesData?.Degree?.queryByPartnerId?.elements ?? [];
  const degrees = allDegrees.filter((degree) => user.isSuperuser() || degree.catalogAvailabilityStatus === 'LIVE');

  // Query for domains
  const { data: domainsData } = useQuery<PartnerDomainsQueryQuery, PartnerDomainsQueryQueryVariables>(
    PartnerDomainsQuery,
    {
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );

  const domains = domainsData?.Domain?.getAllDomains ?? [];

  useEffect(() => {
    // This page can be accessed through CSR routing so we programmatically scroll to the top of the page
    window.scrollTo(0, 0);

    if (partner?.homeLink && typeof window !== 'undefined') {
      // Some partners don't want us to host their page, so we must redirect to their page.
      window.location.assign(partner.homeLink);
    }
  }, [partner]);

  if (partnerLoading) {
    return <Loading />;
  }

  if (partnerError || !partner) {
    if (inServerContext) {
      throw new NotFoundError();
    }
    return <NotFound />;
  }

  if (coursesLoading || specializationsLoading || degreesLoading) {
    return <Loading />;
  }

  if (partner.homeLink) {
    return (
      <p className="redirect-message">
        {_t('Redirecting to')} <a href={partner.homeLink}>{partner.name}</a>…
      </p>
    );
  }

  const { isInternationalSubdomain } = getSubdomainDetails(location);
  const metaTitle = `${partner.name} Online Courses`;

  const instructors = partner.instructors?.elements?.filter((i) => i !== null) ?? [];

  return (
    <div className="rc-PartnerPage">
      <PartnerHero name={partner.name} description={partner.description} squareLogo={partner.squareLogo} />
      <PageGridContainer>
        <Grid item xs={12}>
          <DegreeCardsSection degrees={degrees} title={_t('Degrees')} />
          <SpecializationCardsSection
            specializations={specializations}
            title={_t('Certificates and Specializations')}
          />
          <CourseCardsSection
            courses={courses}
            title={_t('Courses by #{partnerName}', { partnerName: partner.name })}
          />
          <InstructorsCarouselSection instructors={instructors} />
          <ExploreCategoriesSection domains={domains} />
          <PartnerLinksSection links={partner.links} />
        </Grid>
      </PageGridContainer>
      <div>
        <PartnerPageGoogleSchemaMarkup
          partner={{
            ...partner,
            courses: {
              elements: courses.map((course) => ({
                __typename: 'CoursesV1',
                id: course.id,
                slug: course.slug,
              })),
            },
            specializations: {
              elements: specializations.map((specialization) => ({
                __typename: 'OnDemandSpecializationsV1',
                id: specialization.id,
                slug: specialization.slug,
              })),
            },
          }}
        />
        <CourseraMetatags
          metaNameAndProperties={{
            title: metaTitle,
            description: partner.description ?? undefined,
            absoluteImageHref: partner.squareLogo ?? undefined,
            useFollowForDisablingIndexing: true,
          }}
          hrefLangOverrides={(links) => (isInternationalSubdomain ? [] : links)}
        />
      </div>
    </div>
  );
};
export default PartnerPage;
